import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import {
  Inbox as InboxIcon,
  Send as SendIcon,
  FileText,
  Layers,
  Search,
  Plus,
  UserCheck,
  AlertOctagon,
  Archive,
  RotateCcw,
  X,
  CornerUpRight,
  Sparkles,
  CheckSquare,
  FolderPlus,
  ExternalLink,
  Copy,
  Trash2,
  Edit3,
} from "lucide-react";
import { ChannelIcon } from "@/components/BrandIcons";
import { ActionPopover } from "@/components/ActionPopover";
import { relTime, gravatarUrl, initials } from "@/lib/utils-fos";
import { Tip } from "@/components/ui/tooltip";

// Messages — cross-channel triage hub. 4 tabs:
//   Inbox   → things sent to you (populated from Gmail/Slack/WhatsApp/etc.)
//   Sent    → things you sent (from Reachout popup, escalations, or platform sync)
//   Drafts  → AI-drafted or in-progress messages you haven't sent yet
//   Templates → your library of reusable email templates
export default function Messages() {
  const [tab, setTab] = useState("inbox"); // inbox | sent | drafts | templates
  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl mx-auto" data-testid="messages-page">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <InboxIcon size={18} className="text-neutral-500" />
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Messages</h1>
        </div>
        <p className="text-sm text-neutral-500 mt-1">Everything you receive, everything you send — one hub across email, Slack, WhatsApp and more.</p>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-neutral-200">
        <MsgTab active={tab === "inbox"} onClick={() => setTab("inbox")} icon={<InboxIcon size={13} />} testid="msg-tab-inbox">Inbox</MsgTab>
        <MsgTab active={tab === "sent"} onClick={() => setTab("sent")} icon={<SendIcon size={13} />} testid="msg-tab-sent">Sent</MsgTab>
        <MsgTab active={tab === "drafts"} onClick={() => setTab("drafts")} icon={<FileText size={13} />} testid="msg-tab-drafts">Drafts</MsgTab>
        <MsgTab active={tab === "templates"} onClick={() => setTab("templates")} icon={<Layers size={13} />} testid="msg-tab-templates">Email Templates</MsgTab>
      </div>

      {tab === "inbox" && <InboxTab />}
      {tab === "sent" && <SentTab />}
      {tab === "drafts" && <DraftsTab />}
      {tab === "templates" && <TemplatesTab />}
    </div>
  );
}

function MsgTab({ active, onClick, icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
        active ? "border-neutral-900 text-neutral-900 font-semibold" : "border-transparent text-neutral-500 hover:text-neutral-900"
      }`}
    >
      {icon}{children}
    </button>
  );
}

// Inbox — personal @mentions and DMs across your connected channels.
// One triage action per row. Hover over a row to reveal quick-actions.
function InboxTab() {
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [modal, setModal] = useState(null); // { kind, event }
  const [reply, setReply] = useState(null); // ReachOutModal initial for reply flow

  const load = async () => {
    try {
      const [inbox, pp, pr] = await Promise.all([
        endpoints.inbox({ status }),
        endpoints.people(),
        endpoints.projects(),
      ]);
      setItems(inbox); setPeople(pp); setProjects(pr);
    } catch (e) { toast.error("Could not load inbox"); }
  };
  useEffect(() => { load(); }, [status]);

  // Build a ReachOutModal `initial` prefill for the reply flow. Uses the
  // inbound message's own platform so the reply lands on the same channel
  // it came in on.
  const openReply = (event) => {
    const linkedPerson = people.find((p) => (event.people_ids || []).includes(p.id));
    const inbound = (event.source_channel || "email").toLowerCase();
    const platform = ["email", "slack", "whatsapp", "linkedin", "twitter", "sms"].includes(inbound) ? inbound : "email";
    const firstLine = (event.content_summary || "").split("\n")[0].slice(0, 80);
    setReply({
      platform,
      to_person_id: linkedPerson?.id || "",
      to_display: linkedPerson?.email || "",
      subject: platform === "email" && firstLine ? `Re: ${firstLine}` : "",
      body: "",
      related_project_id: (event.project_ids || [])[0] || null,
    });
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const lq = q.toLowerCase();
    return items.filter((e) => (e.content_summary || "").toLowerCase().includes(lq));
  }, [items, q]);

  return (
    <div data-testid="inbox-tab">

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { k: "pending", label: "To triage" },
            { k: "assigned", label: "Assigned" },
            { k: "converted", label: "Converted" },
            { k: "archived", label: "Archived" },
            { k: "all", label: "All" },
          ].map(({ k, label }) => (
            <button key={k} data-active={status === k} onClick={() => setStatus(k)} className="chip" data-testid={`inbox-filter-${k}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[160px] flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-white">
          <Search size={12} className="text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search inbox…" className="flex-1 bg-transparent outline-none text-sm" data-testid="inbox-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="fos-card p-8 text-center">
          <InboxIcon size={28} className="mx-auto text-neutral-300 mb-3" />
          <p className="text-sm text-neutral-600">{status === "pending" ? "Nothing to triage. You're at inbox zero." : `No ${status} messages.`}</p>
          {status === "pending" && (
            <p className="text-xs text-neutral-400 mt-1">Connect Gmail, Slack or WhatsApp from Settings to start ingesting.</p>
          )}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {filtered.map((ev) => (
            <InboxRow
              key={ev.id}
              event={ev}
              people={people}
              projects={projects}
              onOpenModal={(kind) => setModal({ kind, event: ev })}
              onReply={() => openReply(ev)}
              onArchive={async () => { await endpoints.inboxArchive(ev.id); toast.success("Archived"); load(); }}
              onUndo={async () => { await endpoints.inboxUndo(ev.id); toast.success("Moved back to inbox"); load(); }}
              status={status}
            />
          ))}
        </ul>
      )}

      {modal?.kind === "task" && (
        <ConvertToTaskModal event={modal.event} projects={projects} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}
      {modal?.kind === "project" && (
        <ConvertToProjectModal event={modal.event} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}
      {modal?.kind === "assign" && (
        <AssignModal event={modal.event} people={people} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}
      {modal?.kind === "escalate" && (
        <EscalateModal event={modal.event} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}

      {reply && (
        <ReachOutModal
          initial={reply}
          people={people}
          onClose={() => setReply(null)}
          onSent={() => setReply(null)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Inbox row
// -----------------------------------------------------------------------------
function InboxRow({ event, people, projects, onOpenModal, onReply, onArchive, onUndo, status }) {
  const nav = useNavigate();
  const linkedPerson = people.find((p) => (event.people_ids || []).includes(p.id));
  const critical = (event.urgency_score || 0) >= 4;
  const warning = (event.urgency_score || 0) === 3;
  const markerCls = critical ? "critical-marker" : warning ? "warning-marker" : "border-l-2 border-neutral-100";

  const handled = event.inbox_status && event.inbox_status !== "pending";
  const handledLabel = event.inbox_status === "converted" ? "Converted"
    : event.inbox_status === "assigned" ? "Assigned"
    : event.inbox_status === "archived" ? "Archived"
    : null;

  const openLinked = () => {
    if (event.inbox_status === "converted" && event.inbox_action_ref) {
      // Best-effort: jump to project (tasks live under projects). Escalations live under /activity.
      const t = event.inbox_action_ref;
      const project = projects.find((p) => p.id === t);
      if (project) nav(`/projects?focus=${project.id}`);
      else nav(`/activity?focus=${t}`);
    } else if (event.inbox_status === "assigned" && event.inbox_action_ref) {
      nav(`/people?focus=${event.inbox_action_ref}`);
    }
  };

  return (
    <li
      className={`fos-card group p-3 pr-4 flex items-start gap-3 ${markerCls} ${handled ? "opacity-70" : ""}`}
      data-testid={`inbox-row-${event.id}`}
      data-focus-id={event.id}
    >
      {linkedPerson ? <Avatar person={linkedPerson} /> : <ChannelIcon channel={event.source_channel} size={22} className="mt-1" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-snug text-neutral-900">{event.content_summary}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <ChannelIcon channel={event.source_channel} size={11} />
          <span className="text-[11px] text-neutral-500 capitalize">{event.classification || "fyi"}</span>
          {critical && <span className="text-[11px] text-[#FF453A]">Urgency {event.urgency_score}/5</span>}
          <span className="text-[11px] text-neutral-400">·</span>
          <span className="text-[11px] text-neutral-500">{relTime(event.created_at)}</span>
          {handled && (
            <button
              onClick={openLinked}
              className="text-[11px] text-neutral-700 underline decoration-neutral-300 hover:decoration-neutral-900"
            >
              {handledLabel} ›
            </button>
          )}
        </div>
      </div>
      {status === "pending" || !handled ? (
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <Tip label={`Reply on ${platformLabel(event.source_channel)}`}>
            <button
              onClick={onReply}
              data-testid={`inbox-reply-${event.id}`}
              className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center"
              aria-label={`Reply on ${platformLabel(event.source_channel)}`}
            >
              <CornerUpRight size={14} />
            </button>
          </Tip>
          <ActionPopover
            always
            testid={`inbox-actions-${event.id}`}
            items={[
              { icon: <CheckSquare size={14} />, label: "Add to a project as a task", onClick: () => onOpenModal("task"), testid: `inbox-action-task-${event.id}` },
              { icon: <FolderPlus size={14} />, label: "Start a new project", onClick: () => onOpenModal("project"), testid: `inbox-action-project-${event.id}` },
              { icon: <UserCheck size={14} />, label: "Assign to someone", onClick: () => onOpenModal("assign"), testid: `inbox-action-assign-${event.id}` },
              { icon: <AlertOctagon size={14} />, label: "Escalate to founder", onClick: () => onOpenModal("escalate"), testid: `inbox-action-escalate-${event.id}` },
              { icon: <ChannelIcon channel={event.source_channel} size={14} />, label: `Open in ${platformLabel(event.source_channel)}`, onClick: () => openInPlatform(event), testid: `inbox-action-open-${event.id}` },
              { icon: <Archive size={14} />, label: "Archive", onClick: onArchive, testid: `inbox-action-archive-${event.id}` },
            ]}
          />
        </div>
      ) : (
        <ActionPopover
          testid={`inbox-actions-${event.id}`}
          items={[
            { icon: <RotateCcw size={14} />, label: "Move back to inbox", onClick: onUndo, testid: `inbox-action-undo-${event.id}` },
          ]}
        />
      )}
    </li>
  );
}

function Avatar({ person }) {
  const src = person.avatar_url || gravatarUrl(person.email, 48);
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img src={src} alt={person.name} onError={() => setFailed(true)} className="h-9 w-9 rounded-full object-cover shrink-0 border border-neutral-200" />;
  }
  return <div className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 bg-neutral-200 text-neutral-800">{initials(person.name)}</div>;
}

// ---------------------------------------------------------------------------
// Platform helpers — deep-link an item back to its source app.
// ---------------------------------------------------------------------------
function platformLabel(p) {
  return ({ email: "Gmail", slack: "Slack", whatsapp: "WhatsApp", linkedin: "LinkedIn", twitter: "Twitter", sms: "SMS" }[p] || (p || "source"));
}

function openInPlatform(item) {
  const url = item.external_url;
  if (url) { window.open(url, "_blank", "noopener"); return; }
  // Best-effort fallback if the item has no external_url
  const p = item.platform || item.source_channel;
  const fallback = ({
    email: "https://mail.google.com/mail/u/0/#inbox",
    slack: "https://slack.com/",
    whatsapp: "https://web.whatsapp.com/",
    linkedin: "https://www.linkedin.com/messaging/",
    twitter: "https://twitter.com/messages",
    sms: null,
  }[p] || null);
  if (fallback) window.open(fallback, "_blank", "noopener");
  else toast.info(`No deep-link stored for this ${platformLabel(p)} message.`);
}

// ---------------------------------------------------------------------------
// Sent tab — every outbound message across every connected channel.
// ---------------------------------------------------------------------------
function SentTab() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("");

  const load = async () => {
    try {
      const [sent, pp] = await Promise.all([endpoints.sentMessages(), endpoints.people()]);
      setItems(sent); setPeople(pp);
    } catch (e) { toast.error("Could not load sent messages"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = items;
    if (platform) out = out.filter((s) => s.platform === platform);
    if (q.trim()) {
      const s = q.toLowerCase();
      out = out.filter((m) => (m.subject || "").toLowerCase().includes(s) || (m.body || "").toLowerCase().includes(s) || (m.to_display || "").toLowerCase().includes(s));
    }
    return out.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
  }, [items, q, platform]);

  return (
    <div data-testid="sent-tab">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {["", "email", "slack", "whatsapp", "linkedin", "twitter", "sms"].map((k) => (
          <button
            key={k || "all"}
            data-active={platform === k}
            onClick={() => setPlatform(k)}
            className="chip capitalize"
            data-testid={`sent-filter-${k || "all"}`}
          >
            {k ? platformLabel(k) : "All"}
          </button>
        ))}
        <div className="flex-1 min-w-[160px] flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-white">
          <Search size={12} className="text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sent…" className="flex-1 bg-transparent outline-none text-sm" data-testid="sent-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="fos-card p-8 text-center">
          <SendIcon size={20} className="mx-auto mb-2 text-neutral-400" />
          <p className="text-sm text-neutral-500">No sent messages yet.</p>
          <p className="text-xs text-neutral-400 mt-1">Anything you send via the Reachout popup or an Escalation will land here.</p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {filtered.map((m) => {
            const person = people.find((p) => p.id === m.to_person_id);
            return (
              <li key={m.id} className="fos-card group p-3 pr-4 flex items-start gap-3" data-testid={`sent-row-${m.id}`}>
                <ChannelIcon channel={m.platform} size={22} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">To</span>
                    <span className="text-sm font-medium text-neutral-900">{person?.name || m.to_display}</span>
                    <span className="text-[10px] mono uppercase text-neutral-400">{platformLabel(m.platform)}</span>
                  </div>
                  {m.subject && <p className="text-sm mt-0.5 text-neutral-900">{m.subject}</p>}
                  <p className="text-xs text-neutral-600 mt-1 line-clamp-2 whitespace-pre-line">{m.body}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-[11px] text-neutral-500">
                    <span>{relTime(m.sent_at)}</span>
                    {m.related_escalation_id && (
                      <button className="fos-inline-link text-[11px]" onClick={() => nav(`/activity?focus=${m.related_escalation_id}`)}>from escalation</button>
                    )}
                  </div>
                </div>
                <ActionPopover
                  testid={`sent-actions-${m.id}`}
                  items={[
                    { icon: <ChannelIcon channel={m.platform} size={14} />, label: `Open in ${platformLabel(m.platform)}`, onClick: () => openInPlatform(m), testid: `sent-action-open-${m.id}` },
                    { icon: <Copy size={14} />, label: "Copy body", onClick: () => { navigator.clipboard.writeText(m.body); toast.success("Copied to clipboard"); } },
                    { icon: <Trash2 size={14} />, label: "Delete", onClick: async () => { if (!confirm("Delete this sent record?")) return; await endpoints.deleteSentMessage(m.id); toast.success("Deleted"); load(); } },
                  ]}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drafts tab — WIP messages saved (from Reachout popup 'Save draft').
// ---------------------------------------------------------------------------
function DraftsTab() {
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [reachOut, setReachOut] = useState(null); // draft object to edit
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [drafts, pp] = await Promise.all([endpoints.drafts(), endpoints.people()]);
      setItems(drafts); setPeople(pp);
    } catch (e) { toast.error("Could not load drafts"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((d) => (d.subject || "").toLowerCase().includes(s) || (d.body || "").toLowerCase().includes(s) || (d.to_display || "").toLowerCase().includes(s));
  }, [items, q]);

  return (
    <div data-testid="drafts-tab">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex-1 min-w-[160px] flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-white">
          <Search size={12} className="text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search drafts…" className="flex-1 bg-transparent outline-none text-sm" data-testid="drafts-search" />
        </div>
        <Tip label="Compose from scratch">
          <button
            onClick={() => setReachOut({ platform: "email", to_display: "", subject: "", body: "" })}
            data-testid="drafts-new"
            className="text-xs px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-blue-600 hover:bg-neutral-50 font-semibold inline-flex items-center gap-1.5"
          >
            <Plus size={12} /> New draft
          </button>
        </Tip>
      </div>

      {filtered.length === 0 ? (
        <div className="fos-card p-8 text-center">
          <FileText size={20} className="mx-auto mb-2 text-neutral-400" />
          <p className="text-sm text-neutral-500">No drafts.</p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {filtered.map((d) => {
            const person = people.find((p) => p.id === d.to_person_id);
            return (
              <li key={d.id} className="fos-card p-3 flex items-start gap-3" data-testid={`draft-row-${d.id}`}>
                <ChannelIcon channel={d.platform} size={22} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Draft to</span>
                    <span className="text-sm font-medium text-neutral-900">{person?.name || d.to_display || "—"}</span>
                    <span className="text-[10px] mono uppercase text-neutral-400">{platformLabel(d.platform)}</span>
                  </div>
                  {d.subject && <p className="text-sm mt-0.5 text-neutral-900">{d.subject}</p>}
                  <p className="text-xs text-neutral-600 mt-1 line-clamp-2 whitespace-pre-line">{d.body}</p>
                  <div className="text-[11px] text-neutral-500 mt-1.5">Edited {relTime(d.updated_at || d.created_at)}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Tip label="Open compose popup with this draft loaded — pick platform + send.">
                    <button
                      onClick={() => setReachOut(d)}
                      data-testid={`draft-send-${d.id}`}
                      className="text-xs px-2.5 py-1 rounded-md bg-neutral-900 text-white hover:bg-neutral-800 inline-flex items-center gap-1.5"
                    >
                      <SendIcon size={11} /> Send
                    </button>
                  </Tip>
                  <ActionPopover
                    testid={`draft-actions-${d.id}`}
                    items={[
                      { icon: <Edit3 size={14} />, label: "Continue editing", onClick: () => setReachOut(d), testid: `draft-action-edit-${d.id}` },
                      { icon: <Trash2 size={14} />, label: "Delete", onClick: async () => { if (!confirm("Delete draft?")) return; await endpoints.deleteDraft(d.id); toast.success("Draft deleted"); load(); } },
                    ]}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {reachOut && (
        <ReachOutModal
          initial={reachOut}
          people={people}
          onClose={() => setReachOut(null)}
          onSent={async () => { setReachOut(null); await load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email Templates tab — reusable canned messages.
// ---------------------------------------------------------------------------
function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null); // template being edited/created

  const load = async () => {
    try { setItems(await endpoints.emailTemplates()); }
    catch (e) { toast.error("Could not load templates"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((t) => (t.name || "").toLowerCase().includes(s) || (t.subject || "").toLowerCase().includes(s) || (t.body || "").toLowerCase().includes(s));
  }, [items, q]);

  return (
    <div data-testid="templates-tab">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex-1 min-w-[160px] flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-white">
          <Search size={12} className="text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" className="flex-1 bg-transparent outline-none text-sm" data-testid="templates-search" />
        </div>
        <Tip label="Create a new reusable template">
          <button
            onClick={() => setEdit({ name: "", category: "custom", subject: "", body: "" })}
            data-testid="templates-new"
            className="text-xs px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-blue-600 hover:bg-neutral-50 font-semibold inline-flex items-center gap-1.5"
          >
            <Plus size={12} /> New template
          </button>
        </Tip>
      </div>

      {filtered.length === 0 ? (
        <div className="fos-card p-8 text-center">
          <Layers size={20} className="mx-auto mb-2 text-neutral-400" />
          <p className="text-sm text-neutral-500">No templates yet.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((t) => (
            <li key={t.id} className="fos-card p-4" data-testid={`template-row-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-900">{t.name}</p>
                  {t.category && <span className="inline-block text-[10px] mono uppercase text-neutral-500 mt-0.5">{t.category}</span>}
                </div>
                <ActionPopover
                  testid={`template-actions-${t.id}`}
                  items={[
                    { icon: <Edit3 size={14} />, label: "Edit", onClick: () => setEdit(t), testid: `template-action-edit-${t.id}` },
                    { icon: <Copy size={14} />, label: "Copy body", onClick: () => { navigator.clipboard.writeText(t.body); toast.success("Copied"); } },
                    { icon: <Trash2 size={14} />, label: "Delete", onClick: async () => { if (!confirm("Delete template?")) return; await endpoints.deleteEmailTemplate(t.id); toast.success("Deleted"); load(); } },
                  ]}
                />
              </div>
              <p className="text-xs mt-2 font-medium text-neutral-700">{t.subject}</p>
              <p className="text-xs text-neutral-500 mt-1 line-clamp-3 whitespace-pre-line">{t.body}</p>
            </li>
          ))}
        </ul>
      )}

      {edit && (
        <TemplateEditModal
          initial={edit}
          onClose={() => setEdit(null)}
          onSaved={async () => { setEdit(null); await load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reachout / Compose modal — picks a platform, drafts a message, sends.
// Used from: Drafts tab "Continue editing", Escalations "Reach out",
// Escalations "Escalate to founder" (pre-drafted), etc.
// ---------------------------------------------------------------------------
export function ReachOutModal({ initial = {}, people = [], onClose, onSent }) {
  const [platform, setPlatform] = useState(initial.platform || "email");
  const [personId, setPersonId] = useState(initial.to_person_id || "");
  const [customTo, setCustomTo] = useState(initial.to_display || "");
  const [subject, setSubject] = useState(initial.subject || "");
  const [body, setBody] = useState(initial.body || "");
  const [saving, setSaving] = useState(false);
  const draftId = initial.id || null;

  const selectedPerson = people.find((p) => p.id === personId);
  const toDisplay = selectedPerson ? selectedPerson.email : customTo;

  const send = async () => {
    if (!body.trim()) return toast.error("Add a message body");
    if (!toDisplay || toDisplay === "—") return toast.error("Pick a recipient");
    setSaving(true);
    try {
      // For platforms Nango can dispatch (email/slack/whatsapp), send the
      // actual message via the Nango proxy. It also inserts the record into
      // sent_messages, so no separate createSentMessage call is needed.
      const nangoPlatforms = ["email", "slack", "whatsapp"];
      if (nangoPlatforms.includes(platform)) {
        await endpoints.nango.dispatch({
          platform,
          to: toDisplay,
          subject: platform === "email" ? subject : null,
          body,
          to_person_id: personId || null,
          to_display: toDisplay,
          related_escalation_id: initial.related_escalation_id || null,
          related_project_id: initial.related_project_id || null,
        });
      } else {
        // linkedin / twitter / sms — still no live dispatch; record only.
        await endpoints.createSentMessage({
          platform, to_person_id: personId || null, to_display: toDisplay || "—",
          subject: platform === "email" ? subject : null, body,
          related_escalation_id: initial.related_escalation_id || null,
          related_project_id: initial.related_project_id || null,
        });
      }
      if (draftId) await endpoints.deleteDraft(draftId);
      toast.success(`Sent via ${platformLabel(platform)}`);
      onSent();
    } catch (e) {
      // 409 = provider not connected in Nango. Surface a helpful message.
      const detail = e?.response?.data?.detail;
      if (detail?.code === "provider_not_connected") {
        toast.error(detail.message, {
          action: { label: "Open Settings", onClick: () => { window.location.href = "/settings"; } },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Send failed");
      }
    }
    finally { setSaving(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = {
        platform, to_person_id: personId || null, to_display: toDisplay || "",
        subject, body,
        related_escalation_id: initial.related_escalation_id || null,
        related_project_id: initial.related_project_id || null,
      };
      if (draftId) await endpoints.updateDraft(draftId, payload);
      else await endpoints.createDraft(payload);
      toast.success("Saved to drafts");
      onSent();
    } catch (e) { toast.error("Save draft failed"); }
    finally { setSaving(false); }
  };

  const platforms = ["email", "slack", "whatsapp", "linkedin", "twitter", "sms"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="reachout-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">{draftId ? "Continue draft" : "New message"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>

        <Field label="Platform">
          <div className="flex flex-wrap items-center gap-1.5">
            {platforms.map((p) => (
              <button
                key={p}
                onClick={() => setPlatform(p)}
                data-active={platform === p}
                data-testid={`reachout-platform-${p}`}
                className="chip inline-flex items-center gap-1.5 capitalize"
              >
                <ChannelIcon channel={p} size={12} /> {platformLabel(p)}
              </button>
            ))}
          </div>
        </Field>

        <Field label="To">
          {people.length > 0 && (
            <select
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none mb-1.5"
              data-testid="reachout-to-person"
            >
              <option value="">— pick a person —</option>
              {people.map((p) => (<option key={p.id} value={p.id}>{p.name} · {p.role || "—"}</option>))}
            </select>
          )}
          {!personId && (
            <input
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              placeholder={platform === "email" ? "email address" : platform === "whatsapp" || platform === "sms" ? "+1 415 555 0100" : "@handle"}
              className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none"
              data-testid="reachout-to-custom"
            />
          )}
        </Field>

        {platform === "email" && (
          <Field label="Subject">
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none"
              data-testid="reachout-subject"
            />
          </Field>
        )}

        <Field label="Message">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={7}
            placeholder="Write your message…"
            className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none resize-none focus:border-neutral-500"
            data-testid="reachout-body"
          />
        </Field>

        <div className="mt-3 flex items-center gap-2 justify-end">
          <Tip label="Save this message to Drafts. Doesn't send.">
            <button
              onClick={saveDraft}
              disabled={saving || !body.trim()}
              data-testid="reachout-save-draft"
              className="text-sm px-3 py-2 rounded-sm border border-neutral-200 text-blue-600 hover:bg-neutral-50 font-semibold disabled:opacity-40"
            >
              Save draft
            </button>
          </Tip>
          <Tip label={`Sends via ${platformLabel(platform)}. Adds a record to Sent.`}>
            <button
              onClick={send}
              disabled={saving || !body.trim()}
              data-testid="reachout-send"
              className="text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              <SendIcon size={12} /> Send
            </button>
          </Tip>
        </div>
      </div>
    </div>
  );
}

function TemplateEditModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial.name || "", category: initial.category || "custom",
    subject: initial.subject || "", body: initial.body || "",
  });
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Add a name");
    try {
      if (initial.id) await endpoints.updateEmailTemplate(initial.id, form);
      else await endpoints.createEmailTemplate(form);
      toast.success(initial.id ? "Template updated" : "Template created");
      onSaved();
    } catch (e) { toast.error("Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="template-edit-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">{initial.id ? "Edit template" : "New template"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <Field label="Name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 placeholder="e.g. Client kickoff"
                 className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none"
                 data-testid="template-name" />
        </Field>
        <Field label="Category">
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                 placeholder="e.g. client_kickoff"
                 className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none" />
        </Field>
        <Field label="Subject">
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                 placeholder="Use {{first_name}} for placeholders"
                 className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none"
                 data-testid="template-subject" />
        </Field>
        <Field label="Body">
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                    rows={8}
                    className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none resize-none focus:border-neutral-500"
                    data-testid="template-body" />
        </Field>
        <div className="mt-3 flex items-center gap-2 justify-end">
          <button onClick={submit} data-testid="template-save"
                  className="text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 inline-flex items-center gap-1.5">
            Save template
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modals
// -----------------------------------------------------------------------------
function ModalShell({ title, onClose, children, footer, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        {children}
        {footer && <div className="mt-4 flex items-center gap-2">{footer}</div>}
      </div>
    </div>
  );
}

function ConvertToTaskModal({ event, projects, onClose, onDone }) {
  const [projectId, setProjectId] = useState(projects[0]?.id || "");
  const [title, setTitle] = useState(event.content_summary?.slice(0, 120) || "");
  const [priority, setPriority] = useState("medium");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      const r = await endpoints.inboxConvertToTask({ event_id: event.id, project_id: projectId, title, priority });
      toast.success(`Task added to ${r.project_name}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add task"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title="Add to a project as a task" onClose={onClose} testid="inbox-modal-task"
      footer={
        <button onClick={submit} disabled={saving || !projectId} data-testid="inbox-modal-task-submit"
          className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white disabled:opacity-40">
          <CornerUpRight size={12} /> Add task
        </button>
      }>
      <Field label="Project">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none">
          {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
        </select>
      </Field>
      <Field label="Task title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500" />
      </Field>
      <Field label="Priority">
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none">
          <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
        </select>
      </Field>
    </ModalShell>
  );
}

function ConvertToProjectModal({ event, onClose, onDone }) {
  const [name, setName] = useState(event.content_summary?.slice(0, 80) || "New project");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await endpoints.inboxConvertToProject({ event_id: event.id, project_name: name });
      toast.success(`Project "${r.project.name}" created`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create project"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title="Start a new project" onClose={onClose} testid="inbox-modal-project"
      footer={
        <button onClick={submit} disabled={saving || !name.trim()} data-testid="inbox-modal-project-submit"
          className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white disabled:opacity-40">
          <FolderPlus size={12} /> Create project
        </button>
      }>
      <Field label="Project name">
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500" />
      </Field>
      <p className="text-xs text-neutral-500 mt-2">The message summary will be set as the project&apos;s next action.</p>
    </ModalShell>
  );
}

function AssignModal({ event, people, onClose, onDone }) {
  const [personId, setPersonId] = useState(people[0]?.id || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!personId) return;
    setSaving(true);
    try {
      const r = await endpoints.inboxAssign({ event_id: event.id, assignee_person_id: personId, note });
      toast.success(`Assigned to ${r.assignee_name}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title="Assign to someone" onClose={onClose} testid="inbox-modal-assign"
      footer={
        <button onClick={submit} disabled={saving || !personId} data-testid="inbox-modal-assign-submit"
          className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white disabled:opacity-40">
          <UserCheck size={12} /> Assign
        </button>
      }>
      <Field label="Assignee">
        <select value={personId} onChange={(e) => setPersonId(e.target.value)} className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none">
          {people.length === 0 ? <option value="">No people — add some first</option> :
            people.map((p) => (<option key={p.id} value={p.id}>{p.name} — {p.role || "—"}</option>))}
        </select>
      </Field>
      <Field label="Note (optional)">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none resize-none focus:border-neutral-500" placeholder="Add context for the assignee…" />
      </Field>
    </ModalShell>
  );
}

function EscalateModal({ event, onClose, onDone }) {
  const [reason, setReason] = useState(event.content_summary?.slice(0, 200) || "");
  const [urgency, setUrgency] = useState(4);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await endpoints.inboxEscalate({ event_id: event.id, reason, urgency });
      toast.success("Escalation created");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not escalate"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title="Escalate this message" onClose={onClose} testid="inbox-modal-escalate"
      footer={
        <button onClick={submit} disabled={saving || !reason.trim()} data-testid="inbox-modal-escalate-submit"
          className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white disabled:opacity-40">
          <AlertOctagon size={12} /> Create escalation
        </button>
      }>
      <Field label="Reason">
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none resize-none focus:border-neutral-500" />
      </Field>
      <Field label={`Urgency: ${urgency}/5`}>
        <input type="range" min={1} max={5} step={1} value={urgency} onChange={(e) => setUrgency(parseInt(e.target.value))} className="w-full" />
      </Field>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <p className="text-xs text-neutral-500 mb-1">{label}</p>
      {children}
    </div>
  );
}
